// The categories already in use, for the Category field to offer. No Vue, so it
// stays unit-testable.
import { topLevelAcls, type AclLike } from "./acls";

// Uncategorised, from the migration default. normalizeCategory clears it on save,
// so the picker must not offer it.
export const UNCATEGORISED_CATEGORY = "Default";

export interface CategorisedAcl extends AclLike {
  Category?: string | null;
}

export interface KnocCategoryOption {
  // The stored value, trimmed. Picking the option writes exactly this.
  value: string;
  // Knocs already in it, so a typo'd near-duplicate shows a count of 1.
  count: number;
}

// Whether `value` names no category at all.
export function isUncategorised(value: string | null | undefined): boolean {
  const trimmed = (value ?? "").trim();
  return trimmed === "" || trimmed === UNCATEGORISED_CATEGORY;
}

// Distinct categories in use with their knoc counts, sorted by name. Child ACLs
// are dropped, since they inherit the parent's category.
export function knocCategoryOptions(acls: CategorisedAcl[] | null | undefined): KnocCategoryOption[] {
  const counts = new Map<string, number>();
  for (const acl of topLevelAcls(acls)) {
    if (isUncategorised(acl.Category)) continue;
    const value = (acl.Category ?? "").trim();
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return (
    [...counts.entries()]
      .map(([value, count]) => ({ value, count }))
      // Exact compare breaks the case-insensitive tie, or "prod" and "Prod" swap
      // between renders.
      .sort(
        (a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: "base" }) || a.value.localeCompare(b.value),
      )
  );
}

// Options containing `query`, case-insensitively. A blank query matches
// everything, for opening the list without typing.
export function filterKnocCategories(options: KnocCategoryOption[], query: string): KnocCategoryOption[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return options;
  return options.filter((option) => option.value.toLowerCase().includes(needle));
}

// The option `value` names, ignoring case and padding.
function matchKnocCategory(options: KnocCategoryOption[], value: string): KnocCategoryOption | undefined {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return undefined;
  return options.find((option) => option.value.toLowerCase() === trimmed);
}

// Whether saving `value` adds a category rather than reusing one. Case
// insensitive, since that is how near-duplicate spellings multiply.
export function isNewKnocCategory(options: KnocCategoryOption[], value: string): boolean {
  if (isUncategorised(value)) return false;
  return matchKnocCategory(options, value) === undefined;
}

// What to save: the stored spelling when `value` names an existing category,
// otherwise the trimmed value.
export function canonicalKnocCategory(options: KnocCategoryOption[], value: string): string {
  if (isUncategorised(value)) return "";
  return matchKnocCategory(options, value)?.value ?? value.trim();
}
